import { useState } from 'react';
import { addDays, addWeeks, addMonths } from 'date-fns';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Event } from '@/hooks/useEvents';
import { CalendarViewControls, type CalendarViewType } from './CalendarViewControls';
import { MonthView } from './MonthView';
import { WeekView } from './WeekView';
import { ThreeDayView } from './ThreeDayView';
import { DayView } from './DayView';
import { MiniCalendar } from './MiniCalendar';
import { useIsMobile } from '@/hooks/use-mobile';

interface CalendarViewProps {
  events: Event[];
  onDateSelect?: (date: Date) => void;
  onEventSelect?: (event: Event) => void;
  onCreateEvent?: (date?: Date, startTime?: Date, endTime?: Date) => void;
  onUpdateEvent?: (id: string, data: Partial<Event>) => Promise<any>;
}

export function CalendarView({ events, onDateSelect, onEventSelect, onCreateEvent, onUpdateEvent }: CalendarViewProps) {
  const isMobile = useIsMobile();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewType, setViewType] = useState<CalendarViewType>(isMobile ? "day" : "week");

  const handleNavigate = (direction: "prev" | "next") => {
    const increment = direction === "prev" ? -1 : 1;
    
    if (viewType === "day") {
      setCurrentDate(prev => addDays(prev, increment));
    } else if (viewType === "3day") {
      setCurrentDate(prev => addDays(prev, increment * 3));
    } else if (viewType === "week") {
      setCurrentDate(prev => addWeeks(prev, increment));
    } else {
      setCurrentDate(prev => addMonths(prev, increment));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    onDateSelect?.(date);
  };

  const handleCreateEventWithTime = (startTime: Date, endTime: Date) => {
    onCreateEvent?.(startTime, startTime, endTime);
  };

  return (
    <div className="flex gap-6">
      {/* Mini Calendar Sidebar */}
      {!isMobile && (
        <div className="hidden lg:block w-80 flex-shrink-0">
          <MiniCalendar
            events={events}
            currentDate={currentDate}
            onDateSelect={(date) => {
              setCurrentDate(date);
              setViewType('day');
            }}
          />
        </div>
      )}

      {/* Main Calendar View */}
      <div className="flex-1 space-y-6">
        {/* View Controls */}
        <div className="flex flex-wrap items-center justify-between gap-2 no-print">
          <CalendarViewControls
            viewType={viewType}
            onViewChange={setViewType}
            currentDate={currentDate}
            onNavigate={handleNavigate}
            onToday={handleToday}
          />
          <Button onClick={() => onCreateEvent?.(selectedDate || new Date())} className="new-event-button flex-shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            New Event
          </Button>
        </div>

        {/* Conditional View Rendering */}
        {viewType === "month" && (
          <MonthView
            date={currentDate}
            events={events}
            onDateSelect={handleDateClick}
            onEventSelect={onEventSelect}
            onCreateEvent={onCreateEvent}
            selectedDate={selectedDate}
          />
        )}
        {viewType === "week" && (
          <WeekView
            date={currentDate}
            events={events}
            onEventSelect={onEventSelect}
            onCreateEvent={handleCreateEventWithTime}
          />
        )}
        {viewType === "3day" && (
          <ThreeDayView
            date={currentDate}
            events={events}
            onEventSelect={onEventSelect}
            onCreateEvent={handleCreateEventWithTime}
          />
        )}
        {viewType === "day" && (
          <DayView
            date={currentDate}
            events={events}
            onEventSelect={onEventSelect}
            onCreateEvent={handleCreateEventWithTime}
            onUpdateEvent={onUpdateEvent}
          />
        )}
      </div>
    </div>
  );
}